/**
 * OvergrownSkillEffect — Earth tree sculptor path passive (sculptor_path_passive).
 *
 * Enemies that leave the influence radius of any active formation (Sculptor,
 * Pillar, or any future formation type) move 25% slower for 1.5 seconds.
 * The slow applies on exit only — not on entry, not during proximity.
 *
 * The effect exposes `applyExitSlow(targetId)` which the game layer (or
 * TerrainConstructionSystem's exit-detection callback) calls when an enemy
 * crosses from inside a formation's influence zone to outside it.
 *
 * The slow is the standard `'slow'` status type, making it interact with
 * Sunken's bonus entry damage ("enemies already slowed by another terrain
 * effect take +10 damage on Sinkhole entry").
 *
 * UpgradeEffectResolver key(s) on scope 'overgrown':
 * - exitSlowMultiplier: additive, base 0.25 (25% speed reduction → 75% retained)
 * - exitSlowDurationMs: additive, base 1500
 *
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see ADR-065 (CC hierarchy and immunity windows)
 * @see earth-spec.md § Sculptor Path — Overgrown
 */

import type { SkillEffect } from '../../SkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface OvergrownDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
  };
  readonly statusEffects: {
    apply(
      effectType: string,
      params: { durationMs: number; intensity: number; sourceSkillId: string },
      targetId: string,
    ): void;
  };
}

// ---------------------------------------------------------------------------
// Config defaults
// ---------------------------------------------------------------------------

/** Base values so the slow always has meaningful parameters even without upgrades. */
const DEFAULT_CONFIG = {
  exitSlowDurationMs: 1500,
  exitSlowMultiplier: 0.25,
} as const;

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class OvergrownSkillEffect implements SkillEffect {
  readonly skillId = 'sculptor_path_passive';
  readonly type = 'passive' as const;
  readonly activationMode = 'instant' as const;

  private readonly deps: OvergrownDeps;

  private _slowsApplied = 0;

  constructor(deps: OvergrownDeps) {
    this.deps = deps;
  }

  // --- Telemetry getters ---

  /** Total number of exit-slow applications since construction. */
  get slowsApplied(): number { return this._slowsApplied; }

  // --- Exit slow application (called by game layer on formation zone exit) ---

  /**
   * Apply the exit slow to a specific enemy.
   *
   * Called by TerrainConstructionSystem (or a formation influence zone's
   * `onEntityExit` callback) when an enemy crosses from inside a formation's
   * influence radius to outside it.
   *
   * The slow intensity is a reduction fraction: `intensity: 0.25` means
   * speed × (1 - 0.25) = 75% speed. The status effect system interprets
   * this as a standard `'slow'` type.
   */
  applyExitSlow(targetId: string): void {
    const resolvedDuration = this.deps.resolver.getAdditiveEffect('sculptor_path_passive', 'exitSlowDurationMs');
    const resolvedIntensity = this.deps.resolver.getAdditiveEffect('sculptor_path_passive', 'exitSlowMultiplier');
    const durationMs = resolvedDuration !== 0 ? resolvedDuration : DEFAULT_CONFIG.exitSlowDurationMs;
    const intensity = resolvedIntensity !== 0 ? resolvedIntensity : DEFAULT_CONFIG.exitSlowMultiplier;

    this.deps.statusEffects.apply(
      'slow',
      {
        durationMs,
        intensity,
        sourceSkillId: 'sculptor_path_passive',
      },
      targetId,
    );

    this._slowsApplied++;
  }

  // --- SkillEffect interface ---

  update(_delta: number): void {
    // Passive — no per-frame logic. Exit detection is owned by the game layer.
  }

  destroy(): void {
    // No owned resources.
  }
}
